import { Box, Card, Menu, Meter, Stack, Text } from 'grommet';
import { More } from 'grommet-icons';

import { EstimateStatus, Project } from '@talon/web/lib/api';
import { ConfirmModal, FavoriteButton } from '@talon/web/src/components/ui';
import { useProjectStore } from '@talon/web/src/context';
import { observer } from 'mobx-react-lite';
import { ReactNode, useState } from 'react';
import { toast } from 'react-toastify';
import { formatCurrency } from 'src/utils/currency';
import { formatDateOnly } from 'src/utils/dateOnly';

export type ProjectCardProps = {
  project: Project;
  onClick: () => void;
};

const money = { fontVariantNumeric: 'tabular-nums' } as const;

/** "$42.5K": short enough for the middle of the ring */
const compact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** One line of the budget legend: a label (with its ring color) and a right-aligned value */
const LegendRow = ({ label, value, swatch, strong }: { label: string; value: ReactNode; swatch?: string; strong?: boolean }) => (
  <Box direction="row" justify="between" align="center" gap="small">
    <Box direction="row" align="center" gap="xsmall">
      {swatch && <Box width="8px" height="8px" round="2px" background={swatch} flex={false} />}
      <Text size="small" color={strong ? 'text-heading' : 'text-secondary'}>
        {label}
      </Text>
    </Box>
    <Text size="small" color={strong ? 'text-heading' : 'text-secondary'} weight={strong ? 600 : undefined} style={money}>
      {value}
    </Text>
  </Box>
);

/**
 * A project on the home page: what's left of its talent budget, the estimate behind that, and how
 * much of it is cast, all visible without hovering. The whole card opens the project.
 */
export const ProjectCard = observer(({ project, onClick }: ProjectCardProps) => {
  /** Context **/
  const projectStore = useProjectStore();

  /** State **/
  const [confirmArchive, setConfirmArchive] = useState(false);

  /** Computed **/
  const budget = project.talentBudgetInPennies / 100 || 0;
  const finalEstimate = project.estimates.find((estimate) => estimate.status === EstimateStatus.Final);
  const wages = finalEstimate?.total ?? 0;
  const fringe = finalEstimate ? finalEstimate.total_plus_fringe - finalEstimate.total : 0;
  const left = budget - wages - fringe;
  const firstSession = [...project.sessions].sort((a, b) => (a.date > b.date ? 1 : -1))[0];
  const roleCount = project.roles.length;
  const castCount = Math.min(project.contracts.length, roleCount);
  const meta = [project.client?.name, project.product, project.job_code].filter(Boolean).join(' · ');
  const whole = (n: number) => formatCurrency(n, false);

  /** Methods **/
  const archiveProject = async () => {
    setConfirmArchive(false);
    if (!project) {
      toast.error('An internal error has occurred. Please refresh the page and try again.');
      return;
    }
    await projectStore.archiveProject(project.id);
  };

  /** Render **/
  return (
    <>
      <Card
        round="12px"
        border={{ color: 'border-card' }}
        elevation="none"
        background="white"
        pad="20px"
        gap="18px"
        role="link"
        tabIndex={0}
        aria-label={`Open ${project.name}`}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        {/* Title, and what identifies the project */}
        <Box direction="row" justify="between" align="start" gap="small">
          <Box gap="2px">
            <Text size="19px" weight={600} color="text-heading">
              {project.name}
            </Text>
            {meta && (
              <Text size="small" color="text-meta">
                {meta}
              </Text>
            )}
          </Box>
          {/* Clicks and Enter here (and in the menu's drop and dialog) don't open the project; not a tab stop itself */}
          <Box direction="row" align="center" gap="xsmall" flex={false} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
            <FavoriteButton project={project} size="20px" hoveredSize="22px" />
            <Menu
              icon={<More color="text-meta" />}
              a11yTitle={`More actions for ${project.name}`}
              dropAlign={{ top: 'bottom', right: 'right' }}
              dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
              items={[{ label: 'Archive', onClick: () => setConfirmArchive(true) }]}
            />
          </Box>
        </Box>

        {/* Budget: what's left in the ring, and where the rest went beside it */}
        <Box direction="row" gap="medium" align="center">
          <Stack anchor="center">
            <Meter
              type="circle"
              size="112px"
              thickness="12px"
              background="border-divider"
              max={Math.max(budget, wages + fringe) || 1}
              values={[
                { value: Math.max(left, 0), color: 'brand' },
                { value: wages, color: 'chart-wages' },
                { value: fringe, color: 'chart-fringe' },
              ]}
            />
            <Box align="center">
              <Text size="18px" weight={600} color={left < 0 ? 'text-danger' : 'text-heading'} style={money}>
                {budget ? compact.format(Math.abs(left)) : '—'}
              </Text>
              <Text size="xsmall" color="text-meta" style={{ whiteSpace: 'nowrap' }}>
                {!budget ? 'No budget' : left < 0 ? 'over' : 'left'}
              </Text>
            </Box>
          </Stack>
          <Box flex gap="xsmall">
            <LegendRow label="Talent budget" value={budget ? whole(budget) : '—'} strong />
            <LegendRow label="Est. wages" value={finalEstimate ? whole(wages) : '—'} swatch="chart-wages" />
            <LegendRow label="Est. fringe" value={finalEstimate ? whole(fringe) : '—'} swatch="chart-fringe" />
            <LegendRow label="First shoot" value={firstSession ? formatDateOnly(firstSession.date) : '—'} />
          </Box>
        </Box>

        {/* Casting */}
        <Box gap="xsmall">
          <Box direction="row" justify="between">
            <Text size="small" color="text-secondary">
              {roleCount ? `${castCount} of ${roleCount} ${roleCount === 1 ? 'role' : 'roles'} cast` : 'No roles yet'}
            </Text>
            <Text size="small" color="text-secondary">
              {project.is_union ? 'SAG-AFTRA' : 'Non-union'}
            </Text>
          </Box>
          {roleCount > 0 && (
            <Meter
              type="bar"
              round
              thickness="6px"
              size="full"
              background="border-divider"
              max={roleCount}
              values={[{ value: castCount, color: 'bar-ok' }]}
            />
          )}
        </Box>
      </Card>

      {confirmArchive && (
        <ConfirmModal
          title={`Archive ${project.name}?`}
          content="It leaves your project list. Its documents are deleted after your account's retention period (see Account), unless marked Keep. You can reinstate it any time from Archived projects."
          confirmLabel="Archive"
          onConfirm={archiveProject}
          onCancel={() => setConfirmArchive(false)}
        />
      )}
    </>
  );
});
